/**
 * The trader's saved baskets — ROADMAP 9.66.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SERVER IS THE AUTHORITY; THIS IS A CACHE OF IT
 *
 * Every mutation here posts and then stores the SERVER'S answer rather than
 * the body it sent. The two differ in more than an id: the sequence, and so
 * the default name, are assigned server-side, and the leg facts — strike,
 * expiry, lot size — come back off the instrument catalogue rather than off
 * whatever the chain on screen happened to be showing. Merging a local guess
 * in is how a list drifts from the database.
 *
 * It is a plain hook rather than a store because there is exactly one reader:
 * the builder is a single overlay, and a global store for one consumer is a
 * lifetime to manage for nothing.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useState } from 'react'
import { api } from '@/lib/api'
import type { BasketDto, BasketLegDto } from '@/types/basket'

export interface BasketsApi {
  items: readonly BasketDto[]
  loading: boolean
  error: string | null
  /**
   * Start a basket. The server names it unless `seed` says otherwise, and
   * the strategy it carries is provenance — see `basketSeed.ts`.
   */
  create: (
    underlying: string,
    seed?: {
      legs?: readonly BasketLegDto[]
      name?: string
      strategyId?: string | null
    },
  ) => Promise<BasketDto>
  /** Replace a basket's name and legs, whole. See the service's header. */
  save: (
    id: string,
    name: string,
    legs: readonly BasketLegDto[],
  ) => Promise<BasketDto>
  remove: (id: string) => Promise<void>
}

const legWire = (legs: readonly BasketLegDto[]) =>
  legs.map((l) => ({ token: l.token, side: l.side, lots: l.lots }))

const saidWhy = (e: unknown, fallback: string): string =>
  e instanceof Error && e.message ? e.message : fallback

export function useBaskets(enabled: boolean): BasketsApi {
  const [items, setItems] = useState<readonly BasketDto[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)

  /*
   * Fetched when the builder first opens, not on mount — the same rule
   * `StrategyDropdown` follows for the strategy list. Nobody who never opens
   * the builder pays for it.
   */
  useEffect(() => {
    if (!enabled || loaded) return
    let cancelled = false
    setLoading(true)
    void api
      .get<{ items: BasketDto[] }>('/baskets')
      .then((r) => {
        if (cancelled) return
        setItems(r.items)
        setLoaded(true)
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(saidWhy(e, 'Your saved baskets could not be loaded.'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [enabled, loaded])

  /** Put the server's row at the front, replacing any copy we held. */
  const absorb = useCallback((basket: BasketDto): BasketDto => {
    setItems((prev) => [basket, ...prev.filter((b) => b.id !== basket.id)])
    setError(null)
    return basket
  }, [])

  const create = useCallback(
    async (
      underlying: string,
      seed: { legs?: readonly BasketLegDto[]; name?: string; strategyId?: string | null } = {},
    ) => {
      const { basket } = await api.post<{ basket: BasketDto }>('/baskets', {
        underlying,
        legs: legWire(seed.legs ?? []),
        ...(seed.name ? { name: seed.name } : {}),
        ...(seed.strategyId ? { strategyId: seed.strategyId } : {}),
      })
      return absorb(basket)
    },
    [absorb],
  )

  const save = useCallback(
    async (id: string, name: string, legs: readonly BasketLegDto[]) => {
      const { basket } = await api.put<{ basket: BasketDto }>(`/baskets/${id}`, {
        name,
        legs: legWire(legs),
      })
      return absorb(basket)
    },
    [absorb],
  )

  const remove = useCallback(async (id: string) => {
    await api.del(`/baskets/${id}`)
    setItems((prev) => prev.filter((b) => b.id !== id))
  }, [])

  return { items, loading, error, create, save, remove }
}
